import React, { useRef, useState, useEffect } from "react";
import { motion, useScroll, useTransform, useSpring } from "framer-motion";
import MatrixHeading from "./MatrixHeading";
import "./About.css";


const About = () => {
  const sectionRef = useRef(null);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [matrixDone, setMatrixDone] = useState(false);

  /* Detect when the About section enters / leaves the viewport.
     We keep the observer alive so the matrix reveal re-runs every time
     the user scrolls back to this section. */
  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        } else {
          // section left the viewport — reset so the next entry replays
          setIsVisible(false);
          setMatrixDone(false);
        }
      },
      { threshold: 0.25 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  /* Shimmer kicks in once the matrix reveal completes (or on scroll) */
  const showShimmer = matrixDone || isScrolled;

  /* Track scroll through section */
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start 90%", "end 10%"],
  });

  /* Smooth spring on top — tuned to make the heading shrink and GIF grow
     glide gradually rather than snap, even though the scroll trigger range
     is tight:
     - lower stiffness + a little more mass = the spring takes its time
       interpolating to the new target → a slow, fluid visual change
     - overdamped (damping ratio ≈ 1.7) so there's no wobble or bounce
     - tiny restDelta for a clean settle, no perceptible step */
  const smooth = useSpring(scrollYProgress, {
    stiffness: 26,
    damping: 26,
    mass: 1.1,
    restDelta: 0.0001,
  });

  useEffect(() => {
    const unsubscribe = smooth.on("change", (latest) => {
      setIsScrolled(latest > 0.04);
    });
    return () => unsubscribe();
  }, [smooth]);

  /* ── GIF starts at a comfortable size and gently settles to full ── */
  const gifScale = useTransform(smooth, [0, 0.5], [0.88, 1]);
  const gifOpacity = useTransform(smooth, [0, 0.25], [0, 1]);
  /* Audiowide is wider per glyph than Sora, so the same vw values
     render visually larger. Scale both endpoints down to match the
     previous on-screen size. */
  const headingSize = useTransform(smooth, [0, 0.6], ["6.5vw", "4vw"]);
  /* ── Eyebrow / tagline fade in gently ── */
  const eyeOp = useTransform(smooth, [0, 0.08], [0, 1]);

  /* ── Content fades + slides up gradually ── */
  const contentOp = useTransform(smooth, [0.05, 0.35], [0, 1]);

  const contentY = useTransform(smooth, [0.05, 0.35], [40, 0]);
  /* ── Background parallax ── */
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "20%"]);

  /* ── Decorative line width animates ── */
  const lineWidth = useTransform(smooth, [0, 0.06], ["0%", "100%"]);

  return (
    <section id="About" ref={sectionRef}>
      <div className="abt-sticky">
        {/* Background */}
        <motion.div className="abt-bg" style={{ y: bgY }}>
          <div className="abt-bg-grid" />
          <div className="abt-bg-noise" />
          <div className="abt-orb abt-orb-1" />
          <div className="abt-orb abt-orb-2" />
          <div className="abt-orb abt-orb-3" />
        </motion.div>

        {/* Split row */}
        <div className="abt-split">
          {/* LEFT — heading shrinks */}
          <div className="abt-left">

            <motion.h2
              className="abt-main-heading"
              style={{
                fontSize: headingSize,
                color: "#ffffff",
                ...(showShimmer
                  ? {
                      WebkitTextFillColor: "transparent",
                      background:
                        "#ffffff linear-gradient(to right, #ffffff 0%, #ffffff 40%, #ffffff 60%, #ffffff 100%) no-repeat",
                      WebkitBackgroundClip: "text",
                      backgroundClip: "text",
                      backgroundSize: "200% 100%",
                    }
                  : {
                      WebkitTextFillColor: "initial",
                      background: "none",
                      WebkitBackgroundClip: "initial",
                      backgroundClip: "initial",
                    }),
              }}
              animate={
                showShimmer
                  ? {
                      backgroundPositionX: ["-100%", "200%"],
                    }
                  : {
                      backgroundPositionX: "0%",
                    }
              }
              transition={
                showShimmer
                  ? {
                      duration: 1.8,
                      ease: "linear",
                      repeat: Infinity,
                      repeatDelay: 1.6,
                    }
                  : {}
              }
            >
              <MatrixHeading
                text="About Us"
                active={isVisible}
                onComplete={() => setMatrixDone(true)}
              />
            </motion.h2>

            <motion.p className="abt-tagline" style={{ opacity: eyeOp }}>
              <span className="abt-tagline-row">BRIDGING DIVERSE</span>
              <span className="abt-tagline-row">PRINCIPLES</span>
            </motion.p>

            {/* Decorative animated line — sits under the tagline */}
            <motion.div
              className="abt-heading-line"
              style={{ width: lineWidth }}
            />

            {/* Paragraph box under the line */}
            <motion.div
              className="abt-content"
              style={{
                opacity: contentOp,
                y: contentY,
              }}
            >
              <p className="abt-para">
                At IEEE TEMS VIT, our mission is to spark curiosity and inspire
                change. We cultivate an ecosystem where creativity thrives,
                ideas transform into impactful projects, and innovation is truly
                valued. Through engaging workshops, insightful seminars, and
                technology-driven competitions, we empower members to stay ahead
                in an ever-evolving world.
              </p>
            </motion.div>

            {/* Italic mission statement below the paragraph box */}
            <motion.div
              className="abt-mission-left"
              style={{ opacity: contentOp, y: contentY }}
            >
              <p className="abt-mission-text">
                Empowering the next generation of tech leaders through
                innovation, collaboration, and real-world impact.
              </p>
            </motion.div>
          </div>

          {/* RIGHT — GIF only, centered vertically */}
          <div className="abt-right">
            <motion.div
              className="abt-gif-frame"
              style={{
                width: "50vw",
                scale: gifScale,
                opacity: gifOpacity,
              }}
            >
              <img src="about_us.gif" alt="IEEE TEMS" className="abt-gif" />
              <div className="abt-gif-glow" />
              <div className="abt-gif-ring" />
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;
